import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useLang, loc } from "../i18n/LanguageContext";
import { useContent } from "../lib/useContent";
import { Icon, Reveal, MaskLines, useSeo, LazyImg } from "../components/common";
import { SectionHead, HeroButtons, CTABand, BeforeAfter, AISearch } from "../components/blocks";
import { ServiceCard, ProjectCard, StarRow } from "../components/cards";
import { WHY_CHOOSE, INDUSTRIES, IMG, PREMIUM } from "../data/site";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden bg-navy grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.hero} alt="Premium clean modern building" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/60 to-navy/95" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 70% 30%, rgba(30,111,224,0.35), transparent)" }} />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 pt-36 md:pt-44 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 rounded-full glass-dark px-4 py-2 text-xs font-medium text-white/90">
          <span className="h-2 w-2 rounded-full bg-blue animate-pulse" /> {t.hero.badge}
        </motion.div>

        <h1 className="mt-7 font-display text-4xl sm:text-6xl lg:text-7xl text-white leading-[1.02] max-w-4xl">
          <MaskLines lines={t.hero.title} start={0.35} lineClass="" />
        </h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
          className="mt-7 max-w-xl text-lg text-white/75 leading-relaxed">{t.hero.sub}</motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} className="mt-9">
          <HeroButtons />
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-4">
          {Object.values(t.trust).map((label, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-white/80">
              <Icon name="CheckCircle2" size={17} className="text-blue" /> {label}
            </div>
          ))}
        </motion.div>
      </div>

      <div className="absolute bottom-6 inset-x-0 z-10 flex justify-center">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="text-white/50">
          <Icon name="ChevronDown" size={26} />
        </motion.div>
      </div>
    </section>
  );
};

const Manifesto = () => {
  const { t } = useLang();
  const chapters = [
    { n: "01", en: "We show up on time, every time.", nl: "Wij zijn altijd stipt op tijd.", ar: "نحضر في الوقت المحدد دائماً." },
    { n: "02", en: "We use certified eco-friendly products.", nl: "Wij gebruiken gecertificeerde groene producten.", ar: "نستخدم منتجات معتمدة صديقة للبيئة." },
    { n: "03", en: "We guarantee a flawless finish.", nl: "Wij garanderen een vlekkeloos resultaat.", ar: "نضمن نتيجة لا تشوبها شائبة." },
  ];
  const { lang } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 md:py-32">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <Reveal><span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue">Our Promise</span></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 font-display text-3xl md:text-5xl text-navy leading-[1.08]">{t.manifesto}</h2></Reveal>
        </div>
        <div className="md:col-span-7 md:col-start-6">
          <div className="divide-y divide-black/10">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="flex items-start gap-6 py-6">
                  <span className="font-display text-2xl text-blue/40">{c.n}</span>
                  <p className="text-xl md:text-2xl text-navy font-display leading-snug">{c[lang]}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ReviewSlider = ({ reviews }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!reviews.length) return;
    const id = setInterval(() => setI((v) => (v + 1) % reviews.length), 5000);
    return () => clearInterval(id);
  }, [reviews.length]);
  if (!reviews.length) return null;
  const r = reviews[i];
  return (
    <div className="relative mx-auto max-w-3xl text-center" data-testid="review-slider">
      <AnimatePresence mode="wait">
        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}>
          <div className="flex justify-center"><StarRow n={r.rating || 5} size={20} /></div>
          <p className="mt-6 font-display text-2xl md:text-3xl text-navy leading-snug">“{r.text}”</p>
          <div className="mt-6">
            <div className="font-semibold text-navy">{r.name}</div>
            <div className="text-sm text-slate-500">{r.role}</div>
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="mt-8 flex justify-center gap-2">
        {reviews.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} data-testid={`review-dot-${idx}`}
            className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-blue" : "w-2 bg-slate-300"}`} aria-label={`Review ${idx + 1}`} />
        ))}
      </div>
    </div>
  );
};

export default function Home() {
  const { t, lang } = useLang();
  useSeo("Sparko | Premium Cleaning & Maintenance Services in the Netherlands",
    "Sparko delivers premium, eco-friendly professional cleaning across the Netherlands. Fully insured, certified staff. Free quote within 24 hours.");
  const { data: services } = useContent("services");
  const { data: reviews } = useContent("reviews");
  const { data: projects } = useContent("projects");
  const { data: faqs } = useContent("faq");
  const featured = services.filter((s) => s.featured).slice(0, 8);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div>
      <Hero />

      <div className="py-8">
        <Marquee gradient={false} speed={40} className="py-6">
          {PREMIUM.concat(PREMIUM).map((p, i) => (
            <span key={i} className="mx-6 inline-flex items-center gap-3 font-display text-2xl md:text-3xl text-navy/15">
              {p} <Icon name="Sparkles" size={18} className="text-blue/40" />
            </span>
          ))}
        </Marquee>
      </div>

      <Manifesto />

      {/* Services */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 pb-24" data-testid="home-services">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <SectionHead eyebrow={t.sections.servicesTitle} title={t.sections.servicesTitle} sub={t.sections.servicesSub} />
          <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-3 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors shrink-0">
            {t.cta.viewAll} <Icon name="ArrowRight" size={16} className="rtl:rotate-180" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((s, i) => <ServiceCard key={s.id} svc={s} index={i} />)}
        </div>
      </section>

      {/* Why choose - bento */}
      <section className="bg-muted py-24" data-testid="why-choose" style={{ background: "var(--muted)" }}>
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHead center eyebrow="Sparko" title={t.sections.whyTitle} sub={t.sections.whySub} />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.en} delay={(i % 4) * 0.06}>
                <div className="group h-full rounded-3xl bg-white p-6 border border-black/5 hover-lift">
                  <div className="grid place-items-center h-12 w-12 rounded-2xl bg-blue/10 text-blue group-hover:bg-blue group-hover:text-white transition-colors">
                    <Icon name={w.icon} size={22} />
                  </div>
                  <h3 className="mt-5 font-display text-lg text-navy">{w[lang]}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24" data-testid="home-industries">
        <SectionHead eyebrow={t.sections.industriesTitle} title={t.sections.industriesTitle} sub={t.sections.industriesSub} />
        <div className="mt-10 flex flex-wrap gap-3">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.en} delay={(i % 6) * 0.04}>
              <Link to="/industries" className="group inline-flex items-center gap-3 rounded-full border border-navy/10 bg-white px-5 py-3 hover:border-blue hover:bg-blue/5 transition-colors">
                <Icon name={ind.icon} size={18} className="text-blue" />
                <span className="font-medium text-navy">{ind[lang]}</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Before After */}
      <section className="bg-navy py-24 grain" data-testid="home-beforeafter">
        <div className="mx-auto max-w-6xl px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHead light eyebrow={t.sections.beforeAfter} title={t.sections.beforeAfter} sub={t.sections.beforeAfterSub} />
            <div className="mt-6"><HeroButtons /></div>
          </div>
          <Reveal delay={0.1}><BeforeAfter className="aspect-[4/3] shadow-2xl" /></Reveal>
        </div>
      </section>

      {/* Projects */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24" data-testid="home-projects">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <SectionHead eyebrow={t.sections.projectsTitle} title={t.sections.projectsTitle} sub={t.sections.projectsSub} />
          <Link to="/projects" className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-3 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors shrink-0">
            {t.cta.viewAll} <Icon name="ArrowRight" size={16} className="rtl:rotate-180" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {projects.slice(0, 3).map((p, i) => <ProjectCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      {/* AI search */}
      <section className="mx-auto max-w-5xl px-5 md:px-8 pb-24" data-testid="home-ai">
        <AISearch />
      </section>

      {/* Reviews */}
      <section className="bg-muted py-24" data-testid="home-reviews" style={{ background: "var(--muted)" }}>
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHead center eyebrow={t.sections.reviewsTitle} title={t.sections.reviewsTitle} sub={t.sections.reviewsSub} />
          <div className="mt-14"><ReviewSlider reviews={reviews} /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 md:px-8 py-24" data-testid="home-faq">
        <SectionHead center eyebrow="FAQ" title={t.sections.faqTitle} sub={t.sections.faqSub} />
        <div className="mt-10 space-y-3">
          {faqs.slice(0, 5).map((f, i) => (
            <div key={f.id} className="rounded-2xl border border-black/5 bg-white overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between gap-4 p-5 text-start">
                <span className="font-display text-lg text-navy">{loc(f, "q", lang)}</span>
                <Icon name="Plus" size={20} className={`text-blue shrink-0 transition-transform ${openFaq === i ? "rotate-45" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {openFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                    <p className="px-5 pb-5 text-slate-500 leading-relaxed">{loc(f, "a", lang)}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
